import React, { useState, useEffect } from 'react';
import { getUsers, getUserStats, createUser, updateUser, toggleUserStatus, resetUserPassword, getZones } from '../services/api';
import { FiPlus, FiEdit2, FiToggleLeft, FiToggleRight, FiKey, FiSearch } from 'react-icons/fi';

const ROLES = [
    { id: 1, nom: 'superadmin' }, { id: 2, nom: 'admin' }, { id: 3, nom: 'manager' },
    { id: 4, nom: 'supervisor' }, { id: 5, nom: 'commercial' }
];

const Users: React.FC = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [zones, setZones] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [editUser, setEditUser] = useState<any>(null);
    const [resetTarget, setResetTarget] = useState<any>(null);
    const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', password: '', role_id: 5, zone_id: '', manager_id: '' });
    const [newPassword, setNewPassword] = useState('');

    const loadData = () => {
        getUsers({ page, limit: 20, search: search || undefined, role: roleFilter || undefined }).then(res => {
            setUsers(res.data.data); setTotal(res.data.total);
        });
    };

    useEffect(() => {
        getUserStats().then(res => setStats(res.data));
        getZones().then(res => setZones(res.data));
    }, []);

    useEffect(() => { loadData(); }, [page, search, roleFilter]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editUser) {
                await updateUser(editUser.id, form);
            } else {
                await createUser(form);
            }
            setShowModal(false); setEditUser(null); loadData();
        } catch (err: any) {
            alert(err.response?.data?.error || 'Erreur');
        }
    };

    const openEdit = (u: any) => {
        setEditUser(u);
        setForm({ nom: u.nom, prenom: u.prenom, email: u.email, telephone: u.telephone || '', password: '', role_id: ROLES.find(r => r.nom === u.role)?.id || 5, zone_id: u.zone_id || '', manager_id: u.manager_id || '' });
        setShowModal(true);
    };

    const openCreate = () => {
        setEditUser(null);
        setForm({ nom: '', prenom: '', email: '', telephone: '', password: '', role_id: 5, zone_id: '', manager_id: '' });
        setShowModal(true);
    };

    const handleToggle = async (id: number) => {
        await toggleUserStatus(id); loadData();
    };

    const handleResetPassword = async () => {
        if (!newPassword) return;
        await resetUserPassword(resetTarget.id, { password: newPassword });
        setShowResetModal(false); setNewPassword('');
    };

    return (
        <div className="page-users">
            <div className="page-header">
                <h2>Gestion des Utilisateurs</h2>
                <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Nouveau</button>
            </div>

            {stats && (
                <div className="stats-row">
                    <div className="stat-chip">Total: {stats.total}</div>
                    {stats.par_role?.map((r: any) => (
                        <div key={r.role} className="stat-chip">{r.role}: {r.total}</div>
                    ))}
                </div>
            )}

            <div className="filters-bar">
                <div className="search-box">
                    <FiSearch />
                    <input placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
                    <option value="">Tous les roles</option>
                    {ROLES.map(r => <option key={r.nom} value={r.nom}>{r.nom}</option>)}
                </select>
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Nom</th><th>Email</th><th>Role</th><th>Zone</th><th>Statut</th><th>Derniere connexion</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id}>
                                <td><strong>{u.prenom} {u.nom}</strong></td>
                                <td>{u.email}</td>
                                <td><span className="badge">{u.role}</span></td>
                                <td>{u.zone_nom || '-'}</td>
                                <td><span className={`status ${u.actif ? 'active' : 'inactive'}`}>{u.actif ? 'Actif' : 'Inactif'}</span></td>
                                <td>{u.derniere_connexion ? new Date(u.derniere_connexion).toLocaleDateString('fr') : '-'}</td>
                                <td className="actions">
                                    <button className="btn-icon" onClick={() => openEdit(u)} title="Modifier"><FiEdit2 /></button>
                                    <button className="btn-icon" onClick={() => handleToggle(u.id)} title="Activer/Desactiver">
                                        {u.actif ? <FiToggleRight color="#16A34A" /> : <FiToggleLeft color="#999" />}
                                    </button>
                                    <button className="btn-icon" onClick={() => { setResetTarget(u); setShowResetModal(true); }} title="Reset MDP"><FiKey /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Precedent</button>
                <span>Page {page} / {Math.ceil(total / 20)}</span>
                <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Suivant</button>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>{editUser ? 'Modifier' : 'Nouvel'} Utilisateur</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group"><label>Prenom</label><input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} required /></div>
                                <div className="form-group"><label>Nom</label><input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>
                                <div className="form-group"><label>Telephone</label><input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} /></div>
                            </div>
                            {!editUser && <div className="form-group"><label>Mot de passe</label><input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required /></div>}
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Role</label>
                                    <select value={form.role_id} onChange={e => setForm({ ...form, role_id: Number(e.target.value) })}>
                                        {ROLES.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Zone</label>
                                    <select value={form.zone_id} onChange={e => setForm({ ...form, zone_id: e.target.value })}>
                                        <option value="">-- Aucune --</option>
                                        {zones.map((z: any) => <option key={z.id} value={z.id}>{z.nom}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editUser ? 'Mettre a jour' : 'Creer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showResetModal && (
                <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
                    <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
                        <h3>Reinitialiser le mot de passe</h3>
                        <p>Pour: {resetTarget?.prenom} {resetTarget?.nom}</p>
                        <div className="form-group">
                            <label>Nouveau mot de passe</label>
                            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setShowResetModal(false)}>Annuler</button>
                            <button className="btn btn-primary" onClick={handleResetPassword}>Reinitialiser</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
